import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    Tooltip,
    IconButton,
    Autocomplete,
    Button,
    Popper,
    Box,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/system';
import AssetService from '../../../Services/assets.service';
import { useDirectionByLanguage } from '../../../hooks/useDirectionByLanguage';
import {
    stripRelatesToFromDescription,
    appendRelatesToDescription,
    stripSoldOn,
} from '../../../Utils/assetDescriptionUtils';
import { parseApiDateToUtcIso } from '../../../Utils/dateParseUtils';
import SaleTransactionPicker from './SaleTransactionPicker';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';
import { xtramInputSx, xtramDateInputSx, xtramPopperSx } from './AssetModalShell/assetModalInputs';
import AssetVehicleSection, { toVehiclePayload } from './AssetVehicleSection.jsx';
import { ASSET_TYPE } from '../../../Services/expenseAdjustments.service';

const StyledListItem = styled('li')({
    cursor: 'pointer',
});

const StyledPopperOuter = styled(Popper)({
    zIndex: '99999 !important',
    ...xtramPopperSx,
});

const SectionTitle = ({ icon, children }) => (
    <h3 className={shell.sectionTitle}>
        {icon}
        {children}
    </h3>
);

const FieldLabel = ({ required, children }) => (
    <span className={shell.fieldLabel}>
        <span>{required && <span className={shell.requiredMark}>*</span>}{children}</span>
    </span>
);

const sameAssetId = (a, b) => {
    if (a == null || b == null) return false;
    return String(a).toLowerCase() === String(b).toLowerCase();
};

const normalizeRelation = (relation) => {
    if (!relation) return null;
    return {
        ...relation,
        rootAssetId: relation.rootAssetId ?? relation.RootAssetId,
        relatedAssetIds: relation.relatedAssetIds ?? relation.RelatedAssetIds,
        relatedAssets: relation.relatedAssets ?? relation.RelatedAssets,
        rootAsset: relation.rootAsset ?? relation.RootAsset,
    };
};

const getRelationChildIds = (relation) => {
    if (!relation) return [];
    const rel = normalizeRelation(relation);
    const rootId = rel.rootAssetId || rel.rootAsset?.id;
    const relatedIds = rel.relatedAssetIds;
    const relatedAssets = rel.relatedAssets;
    if (Array.isArray(relatedIds) && relatedIds.length > 0) {
        return relatedIds.filter((id) => id && !sameAssetId(id, rootId));
    }
    return (relatedAssets || [])
        .map((ra) => ra?.id ?? ra?.Id)
        .filter((id) => id && !sameAssetId(id, rootId));
};

const mapRelationToRelatedAssets = (relation, allAssets = []) => {
    if (!relation) return [];
    const rel = normalizeRelation(relation);
    const childIds = getRelationChildIds(rel);
    const relatedAssets = rel.relatedAssets || [];
    const seen = new Set();
    const mapped = [];

    const pushMapped = (item) => {
        const id = item?.id ?? item?.Id;
        if (!id || seen.has(String(id).toLowerCase())) return;
        seen.add(String(id).toLowerCase());
        const full = allAssets.find((a) => sameAssetId(a.id, id));
        mapped.push(full || (item?.id ? item : { ...item, id }));
    };

    childIds.forEach((id) => {
        const full = allAssets.find((a) => sameAssetId(a.id, id));
        const summary = relatedAssets.find((ra) => sameAssetId(ra?.id ?? ra?.Id, id));
        pushMapped(full || summary || { id });
    });

    if (mapped.length === 0 && Array.isArray(relatedAssets)) {
        relatedAssets.forEach((ra) => {
            const id = ra?.id ?? ra?.Id;
            if (!id || sameAssetId(id, rel.rootAssetId)) return;
            pushMapped(allAssets.find((a) => sameAssetId(a.id, id)) || ra);
        });
    }

    return mapped;
};

const findRootRelationInList = (relations, rootAssetId) => {
    if (!rootAssetId || !Array.isArray(relations)) return null;
    const found = relations.find((r) =>
        sameAssetId(normalizeRelation(r)?.rootAssetId, rootAssetId)
    );
    return found ? normalizeRelation(found) : null;
};

const findParentRelationInList = (relations, assetId) => {
    if (!assetId || !Array.isArray(relations)) return null;
    const found =
        relations.find((r) => {
            const rel = normalizeRelation(r);
            const related = rel.relatedAssets || [];
            return related.some((ra) => sameAssetId(ra?.id ?? ra?.Id, assetId));
        }) ||
        relations.find((r) => {
            const rel = normalizeRelation(r);
            const rootId = rel.rootAssetId || rel.rootAsset?.id;
            const childIds = getRelationChildIds(rel);
            return childIds.some((id) => sameAssetId(id, assetId)) && !sameAssetId(rootId, assetId);
        });
    return found ? normalizeRelation(found) : null;
};

const relationHasLinkedChildren = (relation) => getRelationChildIds(relation).length > 0;

const EditAsset = ({
    caseId,
    token,
    asset,
    assetGroups,
    assets = [],
    assetRelations = [],
    refreshRelations,
    open,
    onClose,
    onSave,
    onDelete,
    viewDocument,
    isDraggable = true,
    accountantId,
    sortCodeIdOptions = [],
    taxYear,
}) => {

    const { i18n, t } = useTranslation();
    const direction =i18n.dir();
    const [editedAsset, setEditedAsset] = useState({ ...asset });
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [saleTransactionId, setSaleTransactionId] = useState('');
    const [saleTransactionType, setSaleTransactionType] = useState('Incomes');
    const [isLinking, setIsLinking] = useState(false);
    const [showSalePicker, setShowSalePicker] = useState(false);
    const [selectedRelatedAssets, setSelectedRelatedAssets] = useState([]);
    const [selectedAssetsDialogOpen, setSelectedAssetsDialogOpen] = useState(false);
    const [resolvedRelation, setResolvedRelation] = useState(null);

    useEffect(() => {
        if (asset) setEditedAsset({ ...asset });
    }, [asset]);

    const currentRelation = useMemo(() => {
        if (resolvedRelation) return resolvedRelation;
        return findRootRelationInList(assetRelations, asset?.id);
    }, [asset?.id, assetRelations, resolvedRelation]);

    const parentRelation = useMemo(() => {
        return findParentRelationInList(assetRelations, asset?.id);
    }, [asset, assetRelations]);

    const isChildAsset = Boolean(parentRelation);

    const rootAssetForSale = useMemo(() => {
        if (!isChildAsset || !parentRelation) return null;
        const rootId = parentRelation.rootAssetId || parentRelation.rootAsset?.id;
        if (!rootId) return null;
        return (assets || []).find((a) => sameAssetId(a.id, rootId)) || parentRelation.rootAsset || null;
    }, [isChildAsset, parentRelation, assets]);

    const isGroupSoldViaRoot = useMemo(() => {
        if (!rootAssetForSale) return false;
        return !!(
            rootAssetForSale.sellDate ||
            rootAssetForSale.saleIncomeId ||
            rootAssetForSale.saleJournalEntryId
        );
    }, [rootAssetForSale]);

    useEffect(() => {
        if (!open) {
            setResolvedRelation(null);
            setSelectedRelatedAssets([]);
            return;
        }

        if (!asset?.id || !caseId || !token) {
            setResolvedRelation(null);
            setSelectedRelatedAssets([]);
            return;
        }

        if (isChildAsset) {
            setResolvedRelation(null);
            setSelectedRelatedAssets([]);
            return;
        }

        let cancelled = false;

        const applyRelation = (relation) => {
            if (cancelled) return;
            setResolvedRelation(relation || null);
            setSelectedRelatedAssets(mapRelationToRelatedAssets(relation, assets));
        };

        const relationFromProps = findRootRelationInList(assetRelations, asset.id);
        if (relationFromProps && relationHasLinkedChildren(relationFromProps)) {
            applyRelation(relationFromProps);
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            const tryApplyFromCaseRelations = async () => {
                const allResp = await AssetService.GetAssetRelationsByCaseId(caseId, token);
                if (cancelled) return false;
                if (allResp?.success && Array.isArray(allResp.data)) {
                    const found = findRootRelationInList(allResp.data, asset.id);
                    if (found && relationHasLinkedChildren(found)) {
                        applyRelation(found);
                        return true;
                    }
                }
                return false;
            };

            try {
                const resp = await AssetService.GetAssetRelationByRootAsset(caseId, asset.id, token);
                if (cancelled) return;
                if (resp?.success && resp.data) {
                    applyRelation(normalizeRelation(resp.data));
                    return;
                }
            } catch (err) {
                if (cancelled) return;
                try {
                    if (await tryApplyFromCaseRelations()) return;
                } catch (_) {
                    /* fall through */
                }
            }

            try {
                if (await tryApplyFromCaseRelations()) return;
            } catch (_) {
                /* fall through */
            }

            if (relationFromProps) {
                applyRelation(relationFromProps);
            } else {
                applyRelation(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, asset?.id, caseId, token, assetRelations, assets, isChildAsset]);

    useEffect(() => {
        if (!open || isChildAsset || !asset?.id) return;
        const relation = resolvedRelation || findRootRelationInList(assetRelations, asset.id);
        if (!relation || !relationHasLinkedChildren(relation)) return;

        const mapped = mapRelationToRelatedAssets(relation, assets);
        if (mapped.length === 0) return;

        setSelectedRelatedAssets((prev) => {
            const next = mapped.map((m) => assets.find((a) => sameAssetId(a.id, m.id)) || m);
            if (
                prev.length === next.length &&
                prev.every((p, i) => sameAssetId(p?.id, next[i]?.id))
            ) {
                return prev;
            }
            return next;
        });
    }, [open, asset?.id, assets, assetRelations, resolvedRelation, isChildAsset]);

    const eligibleRelatedAssets = useMemo(() => {
        if (!Array.isArray(assets) || !asset?.id) return [];

        const childIdsLinkedElsewhere = new Set();
        const rootIdsOfOtherRelations = new Set();
        (assetRelations || []).forEach((rel) => {
            if (rel?.rootAssetId && !sameAssetId(rel.rootAssetId, asset.id)) {
                rootIdsOfOtherRelations.add(rel.rootAssetId);
            }
            (rel?.relatedAssets || []).forEach((ra) => {
                const raId = ra?.id ?? ra?.Id;
                if (raId && !sameAssetId(rel?.rootAssetId, asset.id)) {
                    childIdsLinkedElsewhere.add(raId);
                }
            });
        });

        const filtered = assets.filter((a) => {
            if (!a || !a.id) return false;
            if (sameAssetId(a.id, asset.id)) return false;
            if ([...childIdsLinkedElsewhere].some((id) => sameAssetId(id, a.id))) return false;
            if ([...rootIdsOfOtherRelations].some((id) => sameAssetId(id, a.id))) return false;
            return true;
        });

        const sameGroupId = asset?.assetGroupId;
        return [...filtered].sort((a, b) => {
            const aSame = sameGroupId && a.assetGroupId === sameGroupId ? 0 : 1;
            const bSame = sameGroupId && b.assetGroupId === sameGroupId ? 0 : 1;
            if (aSame !== bSame) return aSame - bSame;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [assets, assetRelations, asset]);

    const validateFields = () => {
        const { name, originalPrice, depreciationPercentage, purchaseDate } = editedAsset;
        return (
            name?.trim() !== '' &&
            !isNaN(originalPrice) && originalPrice !== null && originalPrice !== undefined &&
            !isNaN(depreciationPercentage) && depreciationPercentage !== null && depreciationPercentage !== undefined &&
            purchaseDate !== null
        );
    };

    useEffect(() => {
        setIsSaveDisabled(!validateFields());
    }, [editedAsset]);

    const inputPropsStyle = { disableUnderline: true, sx: xtramInputSx() };

    const PopperWithDirection = (props) => (
        <StyledPopperOuter {...props} style={{ ...props.style, direction }} placement="bottom-start" disablePortal={false} />
    );

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
            fontSize: 13,
        },
    };

    const applySellDateRemoval = (prev) => ({
        ...prev,
        sellDate: null,
        sellAmount: null,
        saleIncomeId: null,
        saleJournalEntryId: null,
        description: stripSoldOn(prev.description || ''),
    });

    const handleRemoveSellDate = () => {
        if (isChildAsset) return;
        setEditedAsset((prev) => applySellDateRemoval(prev));
    };

    const handleChange = (field, value) => {
        if (field === 'assetGroup') {
            setEditedAsset((prev) => ({
                ...prev,
                depreciationPercentage: value.depreciationPercentage,
                assetGroupId: value?.assetGroupId,
            }));
        } else if (field === 'assetNumber') {
            const matchedAsset = (assets || []).find(
                (a) => a.id !== editedAsset.id && String(a.assetNumber) === String(value)
            );
            const newDesc = matchedAsset
                ? appendRelatesToDescription(editedAsset.description || '', matchedAsset.name)
                : stripRelatesToFromDescription(editedAsset.description || '');
            setEditedAsset((prev) => ({ ...prev, assetNumber: value, description: newDesc }));
        } else if (field === 'sellDate' && !value) {
            if (isChildAsset) return;
            setEditedAsset((prev) => applySellDateRemoval({ ...prev, sellDate: null }));
        } else {
            setEditedAsset((prev) => ({ ...prev, [field]: value }));
        }
    };

    const buildAssetDeleteBlockedSummary = (checkData) => {
        const hasEnhancements = checkData.usedInCapitalGainEnhancements;
        const hasReplacement = checkData.usedInAssetReplacement;

        if (hasEnhancements && hasReplacement) {
            return 'לא ניתן למחוק את הנכס כי הוא מקושר ליתרת השבחות ולשחלוף נכסים.\nיש להסיר קודם את הקישורים מתוך טופס רווח הון.';
        }
        if (hasEnhancements) {
            return 'לא ניתן למחוק את הנכס כי הוא מקושר ליתרת השבחות ברווח הון.\nיש להסיר קודם את הקישור מתוך טופס רווח הון.';
        }
        if (hasReplacement) {
            return 'לא ניתן למחוק את הנכס כי הוא מקושר לשחלוף נכסים ברווח הון.\nיש להסיר קודם את הקישור מתוך טופס רווח הון.';
        }
        return 'הנכס מקושר לתהליך אחר';
    };

    const handleDeleteClick = async () => {
        try {
            const assetId = editedAsset?.id ?? editedAsset?.Id;
            if (!assetId || !caseId || !token) return;

            const checkResp = await AssetService.CanDeleteAsset(caseId, assetId, token);
            const checkData = checkResp?.data;

            if (checkData && checkData.canDelete === false) {
                const reasons = checkData.blockingReasons || [];
                const summary = buildAssetDeleteBlockedSummary(checkData);

                await Swal.fire({
                    icon: 'warning',
                    title: 'לא ניתן למחוק את הנכס',
                    html: `
                        <div style="text-align:right; direction:rtl;">
                            <div style="margin-bottom:12px;">${summary.replace(/\n/g, '<br/>')}</div>
                            ${reasons.length > 0 ? `
                                <ul style="text-align:right; padding-right:20px; margin:0;">
                                    ${reasons.map((r) => `<li>${r}</li>`).join('')}
                                </ul>
                            ` : ''}
                        </div>
                    `,
                    confirmButtonText: 'סגור',
                });
                return;
            }

            const assetLabel = [editedAsset.name, editedAsset.assetNumber]
                .filter(Boolean)
                .join(' - ');

            const result = await Swal.fire({
                title: t('confirmDeleteTitle'),
                html: assetLabel
                    ? `<p style="margin:0 0 8px">${t('AreYouSureDeleteAsset')}</p><p style="margin:0"><strong>${assetLabel}</strong></p>`
                    : `<p style="margin:0">${t('AreYouSureDeleteAsset')}</p>`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d32f2f',
                cancelButtonColor: '#999',
                confirmButtonText: t('Delete'),
                cancelButtonText: t('Cancel'),
            });

            if (!result.isConfirmed) return;
            onDelete(editedAsset.id);
        } catch (error) {
            console.error('Failed to check asset delete permission:', error);
            toast.error('שגיאה בבדיקת אפשרות מחיקת הנכס');
        }
    };

    const handleSave = async () => {
        if (!validateFields()) {
            toast.error(t('Please fill all required fields'));
            return;
        }
        try {
            const cleanedDescription = isChildAsset
                ? stripSoldOn(editedAsset.description || '')
                : editedAsset.sellDate
                    ? (editedAsset.description || '')
                    : stripSoldOn(editedAsset.description || '');

            const updatedAsset = {
                Id: editedAsset.id,
                CaseId: caseId,
                AssetNumber: editedAsset.assetNumber,
                Name: editedAsset.name,
                Description: cleanedDescription,
                PurchaseDate: editedAsset.purchaseDate,
                ActivationDate: editedAsset.activationDate || editedAsset.purchaseDate,
                SellDate: editedAsset.sellDate || null,
                SellAmount: editedAsset.sellAmount
                    ? parseFloat(editedAsset.sellAmount)
                    : null,
                OriginalPrice: parseFloat(editedAsset.originalPrice),
                DepreciationPercentage: parseFloat(editedAsset.depreciationPercentage),
                AssetGroupId: editedAsset.assetGroupId || null,
                ExpenseId: editedAsset.expenseId || null,
                JournalEntryId: editedAsset.journalEntryId || null,
                SaleIncomeId: editedAsset.saleIncomeId || null,
                SaleJournalEntryId: editedAsset.saleJournalEntryId || null,
                AssetType: editedAsset.assetType ?? ASSET_TYPE.GENERAL,
                Vehicle: toVehiclePayload(editedAsset.assetType, editedAsset.vehicle),
            };
            const response = await AssetService.UpdateAsset(updatedAsset, token);
            if (!response.success) {
                toast.error(t('Failed to update asset'));
                return;
            }

            if (!isChildAsset) {
                try {
                    const relationResp = await AssetService.SaveAssetRelation(
                        {
                            caseId,
                            rootAssetId: editedAsset.id,
                            relatedAssetIds: (selectedRelatedAssets || []).map((a) => a.id),
                        },
                        token
                    );
                    if (!relationResp?.success) {
                        toast.error(relationResp?.error || t('Failed to save asset relation'));
                        return;
                    }
                    if (typeof refreshRelations === 'function') {
                        try { await refreshRelations(); } catch (_) { /* non-fatal */ }
                    }
                } catch (relErr) {
                    const message = relErr?.message || t('Failed to save asset relation');
                    toast.error(message);
                    return;
                }
            }

            toast.success(t('Asset updated successfully'));
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            let message = t('An error occurred while updating asset');
            try {
                const parsed = JSON.parse(error?.message || '');
                if (parsed?.error) message = parsed.error;
            } catch (_) {
                if (error?.message && !error.message.startsWith('{')) {
                    message = error.message;
                }
            }
            toast.error(message);
        }
    };

    const handleLinkSaleTransaction = async () => {
        if (!saleTransactionId?.trim() || !editedAsset.sellDate) {
            toast.error(t('FillSellDateAndTransactionId') || 'יש למלא תאריך מכירה ומזהה תנועה');
            return;
        }
        try {
            setIsLinking(true);
            await AssetService.SetEndDate(
                caseId,
                editedAsset.id,
                saleTransactionType,
                saleTransactionId.trim(),
                editedAsset.sellDate,
                token,
                editedAsset.sellAmount ? parseFloat(editedAsset.sellAmount) : null
            );
            toast.success(t('SaleTransactionLinked') || 'תנועת מכירה שויכה בהצלחה');
            const updatedField = saleTransactionType === 'Incomes' ? 'saleIncomeId' : 'saleJournalEntryId';
            const clearedField = saleTransactionType === 'Incomes' ? 'saleJournalEntryId' : 'saleIncomeId';
            setEditedAsset(prev => ({ ...prev, [updatedField]: saleTransactionId.trim(), [clearedField]: null }));
            setSaleTransactionId('');
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToLinkSaleTransaction') || 'שיוך תנועת מכירה נכשל');
        } finally {
            setIsLinking(false);
        }
    };

    const handleUnlinkSaleTransaction = async () => {
        try {
            setIsLinking(true);
            await AssetService.DetachDocumentFromAsset(
                caseId,
                editedAsset.id,
                editedAsset.saleIncomeId || editedAsset.saleJournalEntryId,
                token
            );
            toast.success(t('SaleTransactionUnlinked') || 'שיוך תנועת מכירה הוסר');
            setEditedAsset(prev => ({ ...prev, saleIncomeId: null, saleJournalEntryId: null }));
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToUnlinkSaleTransaction') || 'הסרת שיוך נכשל');
        } finally {
            setIsLinking(false);
        }
    };

    const handleDetachPurchaseDocument = async () => {
        const transactionId = editedAsset.expenseId || editedAsset.journalEntryId;
        if (!transactionId) return;

        const result = await Swal.fire({
            title: t('ConfirmUnlinkPurchaseDocument'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#304FFF',
            cancelButtonColor: '#999',
            confirmButtonText: t('confirm') || t('Confirm') || 'אישור',
            cancelButtonText: t('Cancel') || 'ביטול',
        });

        if (!result.isConfirmed) return;

        try {
            setIsLinking(true);
            const response = await AssetService.DetachDocumentFromAsset(
                caseId,
                editedAsset.id,
                transactionId,
                token
            );
            if (!response?.success) {
                toast.error(t('FailedToUnlinkPurchaseDocument') || 'הסרת קישור מסמך הרכישה נכשלה');
                return;
            }
            toast.success(t('PurchaseDocumentUnlinkedSuccessfully') || 'קישור מסמך הרכישה הוסר בהצלחה');
            const updated = { ...editedAsset, expenseId: null, journalEntryId: null };
            setEditedAsset(updated);
            onSave(updated);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToUnlinkPurchaseDocument') || 'הסרת קישור מסמך הרכישה נכשלה');
        } finally {
            setIsLinking(false);
        }
    };

    const handlePickSaleTransaction = async (selected) => {
        const sellDateIso = editedAsset.sellDate
            || parseApiDateToUtcIso(selected.date)
            || new Date().toISOString();
        const sellAmount = Math.round(Number(selected.amount || 0) * 100) / 100;

        if (!editedAsset.sellDate) {
            handleChange('sellDate', sellDateIso);
        }
        handleChange('sellAmount', sellAmount);
        setSaleTransactionId(selected.id);
        setSaleTransactionType(selected.type === 'Incomes' ? 'Incomes' : 'JournalEntries');

        try {
            setIsLinking(true);
            await AssetService.SetEndDate(
                caseId,
                editedAsset.id,
                selected.type === 'Incomes' ? 'Incomes' : 'JournalEntries',
                selected.id,
                sellDateIso,
                token,
                sellAmount
            );
            toast.success(t('SaleTransactionLinked') || 'תנועת מכירה שויכה בהצלחה');
            const updatedField = selected.type === 'Incomes' ? 'saleIncomeId' : 'saleJournalEntryId';
            const clearedField = selected.type === 'Incomes' ? 'saleJournalEntryId' : 'saleIncomeId';
            setEditedAsset(prev => ({
                ...prev,
                [updatedField]: selected.id,
                [clearedField]: null,
                sellDate: sellDateIso,
                sellAmount,
            }));
            setSaleTransactionId('');
            onSave(editedAsset);
        } catch (error) {
            console.error(error);
            toast.error(t('FailedToLinkSaleTransaction') || 'שיוך תנועת מכירה נכשל');
        } finally {
            setIsLinking(false);
        }
    };

    if (!open) return null;

    const saleDisplaySource = isChildAsset && isGroupSoldViaRoot ? rootAssetForSale : editedAsset;
    const saleFieldsReadOnly = isChildAsset;
    const canRemoveSaleDate =
        !isChildAsset &&
        !!(
            editedAsset?.sellDate ||
            editedAsset?.saleIncomeId ||
            editedAsset?.saleJournalEntryId
        );
    const hasSaleDocument =
        saleDisplaySource?.saleJournalEntryId || saleDisplaySource?.saleIncomeId;
    const hasPurchaseDocument = Boolean(editedAsset.expenseId || editedAsset.journalEntryId);

    const dateHeight = 'var(--xtram-control-height, 40px)';

    const body = (
        <>
            <section className={shell.section}>
                <SectionTitle>{t('AssetInformation') || 'פרטי נכס'}</SectionTitle>
                <div className={shell.fieldGrid}>
                    <div className={shell.field}>
                        <FieldLabel required>{t('AssetName')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('AssetName')}
                            value={editedAsset.name || ''}
                            onChange={(e) => handleChange('name', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </div>
                    <div className={shell.field}>
                        <FieldLabel>{t('SerialNumber')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('SerialNumber')}
                            value={editedAsset.assetNumber || ''}
                            onChange={(e) => handleChange('assetNumber', e.target.value)}
                            variant="standard"
                            type="number"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </div>
                    <div className={shell.field}>
                        <FieldLabel>{t('AssetGroup')}</FieldLabel>
                        <Autocomplete
                            size="small"
                            options={assetGroups}
                            getOptionLabel={(option) => `${option.assetGroupId} - ${option.name}`}
                            value={assetGroups.find((o) => o.assetGroupId === editedAsset.assetGroupId) || null}
                            onChange={(_, newValue) => handleChange('assetGroup', newValue || '')}
                            onInputChange={(_, v) => setSearchTerm(v)}
                            noOptionsText={<strong>{t('NoOptions')}</strong>}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t('Select Asset Group')}
                                    variant="standard"
                                    InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <StyledListItem {...props}>
                                    {`${option.assetGroupId} - ${option.name}`}
                                </StyledListItem>
                            )}
                            PopperComponent={PopperWithDirection}
                        />
                    </div>
                    <div className={shell.field}>
                        <FieldLabel required>{t('DepreciationPercentage')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('DepreciationPercentage')}
                            type="number"
                            value={editedAsset.depreciationPercentage ?? ''}
                            onChange={(e) => handleChange('depreciationPercentage', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </div>
                    <div className={shell.field}>
                        <FieldLabel required>{t('OriginalPrice')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('OriginalPrice')}
                            type="number"
                            value={editedAsset.originalPrice ?? ''}
                            onChange={(e) => handleChange('originalPrice', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </div>
                    <div className={shell.field}>
                        <FieldLabel>{t('Description')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('Description')}
                            value={editedAsset.description || ''}
                            onChange={(e) => handleChange('description', e.target.value)}
                            variant="standard"
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </div>
                </div>
            </section>

            <AssetVehicleSection
                assetType={editedAsset.assetType ?? ASSET_TYPE.GENERAL}
                vehicle={editedAsset.vehicle}
                onChange={(assetType, vehicle) => setEditedAsset((prev) => ({ ...prev, assetType, vehicle }))}
                token={token}
                direction={direction}
                taxYear={taxYear}
            />

            <section className={shell.section}>
                <SectionTitle>{t('Dates') || 'תאריכים'}</SectionTitle>
                <div className={shell.fieldGrid}>
                    <div className={shell.field}>
                        <FieldLabel required>{t('PurchaseDate')}</FieldLabel>
                        <CustomDatePicker
                            date={editedAsset.purchaseDate || null}
                            onDateChange={(date) => handleChange('purchaseDate', date && !isNaN(date.valueOf()) ? date.toISOString() : null)}
                            height={dateHeight}
                            inputSx={xtramDateInputSx()}
                            clearable
                        />
                    </div>
                    <div className={shell.field}>
                        <FieldLabel required>{t('ActivationDate')}</FieldLabel>
                        <CustomDatePicker
                            date={editedAsset.activationDate || null}
                            onDateChange={(date) => handleChange('activationDate', date && !isNaN(date.valueOf()) ? date.toISOString() : null)}
                            height={dateHeight}
                            inputSx={xtramDateInputSx()}
                            clearable
                        />
                    </div>
                </div>
            </section>

            <section className={shell.section}>
                <SectionTitle>{t('SaleInformation') || 'פרטי מכירה'}</SectionTitle>
                <div className={shell.fieldGrid}>
                    <div className={shell.field}>
                        <span className={shell.fieldLabel}>
                            <span>{t('SellDate')}</span>
                            {canRemoveSaleDate && (
                                <button type="button" className={shell.linkBtn} onClick={handleRemoveSellDate}>
                                    {t('RemoveDate')}
                                </button>
                            )}
                        </span>
                        <CustomDatePicker
                            date={saleDisplaySource?.sellDate || null}
                            onDateChange={(date) => handleChange('sellDate', date && !isNaN(date.valueOf()) ? date.toISOString() : null)}
                            height={dateHeight}
                            inputSx={xtramDateInputSx()}
                            clearable={!saleFieldsReadOnly}
                            disabled={saleFieldsReadOnly}
                        />
                    </div>
                    <div className={shell.field}>
                        <FieldLabel>{t('SellAmount')}</FieldLabel>
                        <TextField
                            fullWidth size="small"
                            placeholder={t('SellAmount')}
                            type="number"
                            value={saleDisplaySource?.sellAmount ?? ''}
                            onChange={(e) => handleChange('sellAmount', e.target.value)}
                            variant="standard"
                            disabled={saleFieldsReadOnly || !saleDisplaySource?.sellDate}
                            InputProps={inputPropsStyle}
                            inputProps={inputStyle}
                        />
                    </div>
                </div>

                {hasSaleDocument && (
                    <div className={`${shell.notice} ${shell.noticeDanger}`}>
                        <LinkIcon sx={{ fontSize: 18 }} />
                        <span>{t('LinkedSaleTransaction') || 'תנועת מכירה משויכת'}:</span>
                        <span className={shell.chip}>
                            {saleDisplaySource?.saleIncomeId
                                ? `${t('Income') || 'הכנסה'} - ${saleDisplaySource.saleIncomeId.substring(0, 8)}...`
                                : `${t('JournalEntry') || 'פקודת יומן'} - ${saleDisplaySource?.saleJournalEntryId?.substring(0, 8)}...`}
                        </span>
                        {isDraggable && !isChildAsset && (
                            <span className={shell.toolbarEnd}>
                                <button type="button" className={`${shell.dangerBtn} ${shell.small}`} onClick={() => viewDocument(editedAsset, 'sale')}>
                                    <ReceiptLongIcon />
                                    {t('ViewSaleDocument') || 'צפה בתנועה'}
                                </button>
                                <button type="button" className={`${shell.ghostBtn} ${shell.small}`} onClick={handleUnlinkSaleTransaction} disabled={isLinking}>
                                    <LinkOffIcon />
                                    {t('Unlink') || 'הסר שיוך'}
                                </button>
                            </span>
                        )}
                    </div>
                )}

                {!hasSaleDocument && !isChildAsset && (
                    <div className={`${shell.notice} ${shell.noticeDashed}`}>
                        <div className={shell.toolbar}>
                            <span>{t('LinkSaleTransaction') || 'שיוך תנועת מכירה'}</span>
                            <button
                                type="button"
                                className={`${shell.secondaryBtn} ${shell.small} ${shell.toolbarEnd}`}
                                onClick={() => setShowSalePicker(true)}
                            >
                                <SearchIcon />
                                {t('BrowseTransactions') || 'חפש תנועות לפי מיון'}
                            </button>
                        </div>

                        {editedAsset.sellDate && (
                            <div className={shell.toolbar} style={{ alignItems: 'flex-end' }}>
                                <div className={shell.field} style={{ minWidth: 130 }}>
                                    <span className={shell.fieldLabel}>{t('TransactionType') || 'סוג תנועה'}</span>
                                    <TextField
                                        select size="small" variant="standard"
                                        value={saleTransactionType}
                                        onChange={(e) => setSaleTransactionType(e.target.value)}
                                        InputProps={inputPropsStyle}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="Incomes">{t('Income') || 'הכנסה'}</option>
                                        <option value="JournalEntries">{t('JournalEntry') || 'פקודת יומן'}</option>
                                    </TextField>
                                </div>
                                <div className={shell.field} style={{ flex: 1, minWidth: 200 }}>
                                    <span className={shell.fieldLabel}>{t('TransactionId') || 'מזהה תנועה (ID)'}</span>
                                    <TextField
                                        fullWidth size="small" variant="standard"
                                        placeholder={t('PasteTransactionId') || 'הדבק מזהה תנועה'}
                                        value={saleTransactionId}
                                        onChange={(e) => setSaleTransactionId(e.target.value)}
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className={shell.blueBtn}
                                    onClick={handleLinkSaleTransaction}
                                    disabled={isLinking || !saleTransactionId.trim()}
                                >
                                    <LinkIcon />
                                    {isLinking ? (t('Linking') || 'משייך...') : (t('LinkTransaction') || 'שייך תנועה')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </section>

            <section className={shell.section}>
                <SectionTitle>{t('Linking') || 'שיוך'}</SectionTitle>
                <div className={shell.field}>
                    <FieldLabel>{t('RelatedAssets') || 'נכסים מקושרים'}</FieldLabel>
                    {isChildAsset ? (
                        <span className={shell.chip} style={{ alignSelf: 'flex-start' }}>
                            <LinkIcon sx={{ fontSize: 16 }} />
                            {`${t('LinkedTo') || 'מקושר ל'}-${parentRelation?.rootAsset?.name || ''}`}
                        </span>
                    ) : (
                        <Autocomplete
                            multiple
                            size="small"
                            disableCloseOnSelect
                            options={eligibleRelatedAssets}
                            value={selectedRelatedAssets}
                            getOptionLabel={(option) => `${option.name} - ${option.assetNumber}`}
                            isOptionEqualToValue={(option, value) => sameAssetId(option?.id, value?.id)}
                            onChange={(_, newValue) => setSelectedRelatedAssets(newValue || [])}
                            noOptionsText={<strong>{t('NoOptions')}</strong>}
                            renderTags={(value, getTagProps) => {
                                const visible = value.slice(0, 3);
                                const hiddenCount = value.length - visible.length;
                                return (
                                    <>
                                        {visible.map((option, index) => {
                                            const tagProps = getTagProps({ index });
                                            return (
                                                <Chip
                                                    {...tagProps}
                                                    key={option.id}
                                                    size="small"
                                                    label={`${option.name} - ${option.assetNumber || ''}`}
                                                    sx={{ maxWidth: 180, backgroundColor: 'rgba(48,79,255,0.08)', color: 'var(--xtram-blue)', fontWeight: 600 }}
                                                />
                                            );
                                        })}
                                        {hiddenCount > 0 && (
                                            <Chip
                                                size="small"
                                                clickable
                                                label={`+${hiddenCount}`}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedAssetsDialogOpen(true);
                                                }}
                                                sx={{ backgroundColor: 'var(--xtram-orange)', color: '#fff', fontWeight: 700 }}
                                            />
                                        )}
                                    </>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={selectedRelatedAssets.length === 0 ? (t('SelectRelatedAssets') || 'בחר נכסים מקושרים') : ''}
                                    variant="standard"
                                    InputProps={{
                                        ...params.InputProps,
                                        ...inputPropsStyle,
                                        sx: {
                                            ...inputPropsStyle.sx,
                                            overflow: 'hidden',
                                            flexWrap: 'nowrap',
                                        },
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <StyledListItem {...props}>
                                    {`${option.name} - ${option.assetNumber}`}
                                </StyledListItem>
                            )}
                            PopperComponent={PopperWithDirection}
                        />
                    )}
                </div>

                {isDraggable && hasPurchaseDocument && (
                    <div className={shell.toolbar} style={{ marginTop: 12 }}>
                        <button type="button" className={`${shell.secondaryBtn} ${shell.small}`} onClick={() => viewDocument(editedAsset)}>
                            <DescriptionIcon />
                            {t('ViewPurchaseDocument') || 'צפה בתנועת רכישה'}
                        </button>
                        <button type="button" className={`${shell.dangerBtn} ${shell.small}`} onClick={handleDetachPurchaseDocument} disabled={isLinking}>
                            <LinkOffIcon />
                            {t('UnlinkPurchaseDocument') || 'נתק מסמך רכישה'}
                        </button>
                    </div>
                )}
            </section>
        </>
    );

    const footerStart = (
        <button type="button" className={shell.dangerBtn} onClick={handleDeleteClick}>
            <DeleteIcon />
            {t('Delete')}
        </button>
    );

    const footer = (
        <>
            <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                {t('Cancel')}
            </button>
            <button type="button" className={shell.primaryBtn} onClick={handleSave} disabled={isSaveDisabled}>
                <SaveIcon />
                {t('Save')}
            </button>
        </>
    );

    const subtitle = editedAsset?.name
        ? `${editedAsset.assetNumber ? `${editedAsset.assetNumber} - ` : ''}${editedAsset.name}`
        : null;

    return (
        <>
            {isDraggable ? (
                <AssetModalShell
                    open={open}
                    onClose={onClose}
                    title={t('EditAsset')}
                    subtitle={subtitle}
                    direction={direction}
                    size="md"
                    footerStart={footerStart}
                    footer={footer}
                >
                    {body}
                </AssetModalShell>
            ) : (
                <div className={shell.stack} dir={direction}>
                    <div className={shell.toolbar}>
                        <h3 className={shell.sectionTitle} style={{ margin: 0 }}>{t('EditAsset')}</h3>
                        <Tooltip title={t('Exit')}>
                            <IconButton onClick={onClose} className={`${shell.iconBtn} ${shell.toolbarEnd}`}>
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div>{body}</div>
                    <div className={shell.footer} style={{ marginTop: 0 }}>
                        <div className={shell.footerStart}>{footerStart}</div>
                        {footer}
                    </div>
                </div>
            )}
            <SaleTransactionPicker
                open={showSalePicker}
                onClose={() => setShowSalePicker(false)}
                onSelect={handlePickSaleTransaction}
                caseId={caseId}
                accountantId={accountantId}
                token={token}
                sortCodeOptions={sortCodeIdOptions}
            />

            <Dialog
                open={selectedAssetsDialogOpen}
                onClose={() => setSelectedAssetsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                // Must sit above the AssetModalShell overlay that hosts EditAsset.
                sx={{ zIndex: 100000 }}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid var(--xtram-blue)',
                        boxShadow: '0 16px 48px rgba(48, 79, 255, 0.22), 0 4px 12px rgba(0,0,0,0.08)',
                    },
                    dir: direction,
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 2.5,
                        py: 1.5,
                        background: '#fff',
                        color: 'var(--xtram-blue)',
                        borderBottom: '1px solid #e5e7f6',
                        fontFamily: 'var(--xtram-font)',
                    }}
                    dir={direction}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                            {t('SelectedAssets') || 'הנכסים שנבחרו'}
                        </Typography>
                        <Box
                            sx={{
                                ml: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: '999px',
                                backgroundColor: 'var(--xtram-orange)',
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: 1.4,
                            }}
                        >
                            {selectedRelatedAssets.length}
                        </Box>
                    </Box>
                    <Tooltip title={t('Close') || 'סגור'}>
                        <IconButton
                            size="small"
                            onClick={() => setSelectedAssetsDialogOpen(false)}
                            aria-label="close"
                            sx={{
                                color: 'var(--xtram-blue)',
                                '&:hover': { backgroundColor: 'rgba(48,79,255,0.08)' },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>

                <DialogContent
                    sx={{
                        p: 0,
                        backgroundColor: '#fafbff',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                    }}
                    dir={direction}
                >
                    {selectedRelatedAssets.length === 0 ? (
                        <Box
                            sx={{
                                py: 5,
                                px: 3,
                                textAlign: 'center',
                                color: 'text.secondary',
                                fontWeight: 500,
                            }}
                        >
                            {t('NoOptions')}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 2 }}>
                            {[...selectedRelatedAssets]
                                .sort((a, b) => {
                                    const an = Number(a?.assetNumber);
                                    const bn = Number(b?.assetNumber);
                                    const aNum = Number.isFinite(an);
                                    const bNum = Number.isFinite(bn);
                                    if (aNum && bNum) return an - bn;
                                    if (aNum) return -1;
                                    if (bNum) return 1;
                                    return String(a?.assetNumber ?? '').localeCompare(String(b?.assetNumber ?? ''));
                                })
                                .map((option) => (
                                <Box
                                    key={option.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                        backgroundColor: '#fff',
                                        border: '1px solid #e0e4f5',
                                        borderRadius: '10px',
                                        px: 1.5,
                                        py: 1,
                                        transition: 'all 0.15s ease-in-out',
                                        '&:hover': {
                                            borderColor: '#304FFF',
                                            boxShadow: '0 2px 8px rgba(48,79,255,0.10)',
                                        },
                                    }}
                                    dir={direction}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            minWidth: 0,
                                            flex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: '6px',
                                                backgroundColor: '#E8EBFF',
                                                color: '#304FFF',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                lineHeight: 1.4,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {option.assetNumber || '—'} -
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: 14,
                                                color: '#1f2447',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {option.name}
                                        </Typography>

                                    </Box>
                                    <Tooltip title={t('Remove') || 'הסר'}>
                                        <IconButton
                                            size="small"
                                            aria-label="remove"
                                            onClick={() =>
                                                setSelectedRelatedAssets((prev) =>
                                                    prev.filter((a) => a.id !== option.id)
                                                )
                                            }
                                            sx={{
                                                color: '#9099b7',
                                                flexShrink: 0,
                                                '&:hover': {
                                                    color: '#d32f2f',
                                                    backgroundColor: 'rgba(211,47,47,0.08)',
                                                },
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderTop: '1px solid #e0e4f5',
                        backgroundColor: '#fff',
                    }}
                    dir={direction}
                >
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setSelectedAssetsDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontFamily: 'var(--xtram-font)',
                            borderRadius: '10px',
                            px: 2.5,
                            py: 0.8,
                            backgroundColor: 'var(--xtram-orange)',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'var(--xtram-orange-hover)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {t('Close') || 'סגור'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditAsset;
